import { Dialog, IconButton } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbCirclePlus } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import {
  BAAS_ACCOUNT_VIRTUAL_TYPE,
  createBaasAccount,
  getBaasAccountEntities,
} from "../../../../../Services/baasAccountsListApi";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

/** Exit-rail virtuals (WALLET) are BR/BRL only — Pix exits via the instance Root rail. */
const VIRTUAL_CURRENCY = "BRL";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const chipClass = (selected) =>
  [
    "inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
    selected
      ? "border-[#e7a73f] bg-[#e7a73f]/15 text-[#b87a1f] dark:text-[#e7a73f]"
      : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300",
  ].join(" ");

const entityMatchesSearch = (entity, query) => {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const haystack = [entity?.name, entity?.document, entity?.id]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return haystack.includes(q);
};

/**
 * Currency-only create for exit-rail WALLET virtuals.
 * Entity picker uses instance-linked entities (BaasApiInstancePerson /
 * BaasApiInstanceAccounts via console API key) — same scope as Contas listing.
 * No OTC / instance-shell type picker — only entity + BRL.
 */
export const BaasVirtualAccountCreateModal = ({
  open,
  onClose,
  initialEntityId = "",
  onCreated,
}) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();

  const [entities, setEntities] = useState([]);
  const [entitySearch, setEntitySearch] = useState("");
  const [entityId, setEntityId] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!open || !selectedInstance) {
      setEntities([]);
      return undefined;
    }

    (async () => {
      try {
        // Instance scope only — same as BaasAccountCreateModal / Contas list.
        // Do NOT filter by ownership_document (logged-in CPF/CNPJ).
        const nextEntities = await getBaasAccountEntities(selectedInstance);
        if (!cancelled) setEntities(nextEntities);
      } catch {
        if (!cancelled) setEntities([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedInstance]);

  useEffect(() => {
    if (!open) {
      setEntityId("");
      setEntitySearch("");
      setLoading(false);
      setFormError(null);
      return;
    }

    const preferred =
      (initialEntityId && entities.some((entity) => entity.id === initialEntityId)
        ? initialEntityId
        : "") ||
      entities[0]?.id ||
      "";
    setEntityId(preferred);
    setEntitySearch("");
    setLoading(false);
    setFormError(null);
  }, [open, initialEntityId, entities]);

  const filteredEntities = useMemo(
    () => entities.filter((entity) => entityMatchesSearch(entity, entitySearch)),
    [entities, entitySearch],
  );

  const selectedEntity = useMemo(
    () => entities.find((entity) => entity.id === entityId) || null,
    [entities, entityId],
  );

  const canSubmit = Boolean(entityId) && !loading;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !selectedInstance?.baasInstanceId) return;

    setLoading(true);
    setFormError(null);
    try {
      const account = await createBaasAccount(selectedInstance, {
        entityId,
        entityName: selectedEntity?.name || "",
        type: BAAS_ACCOUNT_VIRTUAL_TYPE,
        currencies: [VIRTUAL_CURRENCY],
        countryCode: "BR",
      });

      if (!account) {
        setFormError(intl.get("baas.accounts.virtualCreate.error"));
        return;
      }

      addToast({
        type: "success",
        title: intl.get("baas.accounts.virtualCreate.success"),
      });
      onCreated?.(account);
      onClose?.();
    } catch (error) {
      setFormError(
        error?.message ||
          error?.body?.message ||
          intl.get("baas.accounts.virtualCreate.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <TbCirclePlus className="h-5 w-5 shrink-0 text-[#e7a73f]" aria-hidden />
              <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
                {intl.get("baas.accounts.virtualCreate.pageTitle")}
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.accounts.virtualCreate.subtitle")}
            </p>
          </div>
          <IconButton
            type="button"
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label={intl.get("baas.accounts.virtualCreate.cancel")}
          >
            <MdClose />
          </IconButton>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          {formError ? (
            <p
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
            >
              {formError}
            </p>
          ) : null}
          <div>
            <label
              htmlFor="baas-virtual-create-entity-search"
              className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {intl.get("baas.accounts.create.entityLabel")}
            </label>
            <input
              id="baas-virtual-create-entity-search"
              type="search"
              className={inputClass}
              value={entitySearch}
              onChange={(event) => setEntitySearch(event.target.value)}
              placeholder={intl.get("baas.accounts.create.entitySearchPlaceholder")}
              autoComplete="off"
            />
            <select
              id="baas-virtual-create-entity"
              className={`${inputClass} mt-2`}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              required
            >
              <option value="">{intl.get("baas.accounts.create.entityPlaceholder")}</option>
              {filteredEntities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.document
                    ? `${entity.name} · ${entity.document}`
                    : entity.name}
                </option>
              ))}
            </select>
            {entitySearch.trim() && filteredEntities.length === 0 ? (
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                {intl.get("baas.accounts.create.entitySearchEmpty")}
              </p>
            ) : null}
            {!entitySearch.trim() && entities.length === 0 ? (
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                {intl.get("baas.accounts.virtualCreate.entitiesEmpty")}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.accounts.create.currencyLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chipClass(true)} aria-pressed>
                {VIRTUAL_CURRENCY}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              {intl.get("baas.accounts.virtualCreate.currencyHint")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <button
            type="button"
            className="btn-outline w-auto"
            onClick={onClose}
            disabled={loading}
          >
            {intl.get("baas.accounts.virtualCreate.cancel")}
          </button>
          <button type="submit" className="btn-primary w-auto" disabled={!canSubmit}>
            {loading
              ? intl.get("baas.accounts.virtualCreate.submitting")
              : intl.get("baas.accounts.virtualCreate.submit")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default BaasVirtualAccountCreateModal;
